/**
 * The account control, React edition of ui/AccountMenu.ts.
 *
 * Same shape as before (and as Aladdin): a "Sign in" trigger opening a menu
 * of two doors, then the avatar opening a menu of one. The anonymous door is
 * warned both ways - confirmGuest/confirmSignOut still own those dialogs.
 * What React buys here is the death of the hand-rolled paint(): the menu is
 * a projection of auth state, and a sign-in that happens anywhere (the
 * assistant's gate, a redirect landing) re-renders it through useAuthTick.
 */

import { useEffect, useRef, useState } from 'react'
import { auth, type Account } from '../../auth/Auth'
import { t } from '../../i18n/i18n'
import { confirmGuest, confirmSignOut } from '../../ui/SignInGate'
import { useLang } from '../i18n/LanguageProvider'
import { useAuthTick } from '../state/hooks'

function initial(name: string): string {
  return (name.trim()[0] ?? '·').toUpperCase()
}

function Avatar({ account, className }: { account: Account; className: string }) {
  return account.photoURL ? (
    <img className={className} src={account.photoURL} alt="" referrerPolicy="no-referrer" />
  ) : (
    <span className={`${className} placeholder`}>
      {account.anonymous ? '?' : initial(account.name)}
    </span>
  )
}

/** Google's mark, drawn inline - a classroom network may not reach the CDN. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  )
}

export function AccountControl() {
  useLang()
  const authTick = useAuthTick()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Whatever the menu was offering is answered by any auth movement, so it
  // must not stay open over a state it no longer describes.
  useEffect(() => {
    setOpen(false)
  }, [authTick])

  // Dismissal, registered only while the menu is up. Capture phase, because
  // the viewport swallows pointer events to look around.
  useEffect(() => {
    if (!open) return
    const onOutside = (e: PointerEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onEscape = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      // Escape closes the tool panel too. The nearest thing wins.
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('pointerdown', onOutside, true)
    window.addEventListener('keydown', onEscape, true)
    return () => {
      document.removeEventListener('pointerdown', onOutside, true)
      window.removeEventListener('keydown', onEscape, true)
    }
  }, [open])

  useEffect(() => {
    void auth.start()
  }, [])

  const account = auth.account

  return (
    <div
      ref={rootRef}
      className="account"
      // The entry screen is one big click target for walking into the fab.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {account ? (
        <button
          className="account-trigger avatar"
          aria-haspopup="menu"
          aria-expanded={open}
          title={
            account.anonymous
              ? t('auth.SignedInAsGuest')
              : (account.email ?? t('auth.SignedInAs', { name: account.name }))
          }
          onClick={() => setOpen(!open)}
        >
          <Avatar account={account} className="account-avatar" />
        </button>
      ) : (
        <button
          className="account-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          // Nothing to offer until the SDK has answered. It resolves during
          // the seconds a student spends on the entry screen.
          disabled={auth.phase === 'loading' || auth.pending}
          onClick={() => setOpen(!open)}
        >
          {t('auth.SignIn')}
        </button>
      )}
      {open && (account ? <SignedInMenu account={account} /> : <SignedOutMenu />)}
    </div>
  )
}

function SignedOutMenu() {
  return (
    <div className="account-menu" role="menu">
      <button
        className="account-item"
        onClick={() => {
          void auth.signInWithGoogle()
        }}
      >
        <GoogleMark />
        {t('auth.AsMe')}
      </button>
      <button
        className="account-item"
        onClick={() => {
          void confirmGuest()
        }}
      >
        <span className="account-glyph">?</span>
        {t('auth.Anonymously')}
      </button>
      <div className="account-sep" />
      <div className="account-note">{t('auth.MenuWhy')}</div>
      {auth.error ? <div className="account-note error">{auth.error}</div> : null}
    </div>
  )
}

function SignedInMenu({ account }: { account: Account }) {
  return (
    <div className="account-menu" role="menu">
      <div className="account-head">
        <Avatar account={account} className="account-avatar" />
        <div className="account-id">
          <div className="account-who">
            {account.anonymous ? t('auth.GuestName') : account.name}
          </div>
          <div className="account-sub">
            {account.anonymous ? t('auth.GuestScope') : (account.email ?? '')}
          </div>
        </div>
      </div>
      <div className="account-sep" />
      <button
        className="account-item"
        disabled={auth.pending}
        onClick={() => {
          void confirmSignOut()
        }}
      >
        {t('auth.SignOut')}
      </button>
    </div>
  )
}
